import { styled } from "styled-components";
import { UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import {
  DataSourceStatus,
  DatabaseType,
  FileType,
  IDataSource,
  IDataSourceApp,
  IPageableResponse,
  ISearchPayload,
  Loading,
  QUERIES,
  chartType,
  checkSearchSession,
  getLatestSearch,
  getRecentSearch,
  getSearchSession,
  getSearchSettings,
  openNotification,
  useAppLayout,
  isEmpty,
} from "@dataone/lib";
import { Row, Col, Button, Tooltip, notification, Skeleton } from "antd";
import { MutableRefObject, Suspense, useState } from "react";
import DataSourcesModal from "../../modal/DataSourcesModal";
import DataSourceModal from "../../../../../../data-center/components/Modal/DataSourceModal";
import DataSourceStepsForm from "../../../../../../data-center/components/steps/DataSourceStepsForm";
import {
  CsvIcon,
  ExcelIcon,
  MysqlIcon,
  NotificationIcon,
  PlusIcon,
  PostgresqlIcon,
  SageIcon,
  SapIcon,
  SqlServerIcon,
} from "@dataone/icons";
import { useNavigate } from "react-router-dom";
import DatabaseSession from "../database-session/DatabaseSession";

interface DatabaseListBodyProps {
  selectedDB: IDataSource | null;
  stepRef3: MutableRefObject<null>;
  stepRef4: MutableRefObject<null>;
  isModalOpen: boolean;
  isOpen: boolean;
  datasources: IPageableResponse<Required<IDataSource>> | undefined;
  isLoading: boolean;
  setSelectedDB: React.Dispatch<React.SetStateAction<IDataSource | null>>;
  setResponse: React.Dispatch<React.SetStateAction<Partial<ISearchPayload>>>;
  setShowResponse: React.Dispatch<React.SetStateAction<boolean>>;
  setShowComponents: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateSuccess: () => void;
  refetchDatasets: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const DatabaseListBody = ({
  selectedDB,
  stepRef3,
  stepRef4,
  isOpen,
  isModalOpen,
  datasources,
  isLoading,
  setOpen,
  setSelectedDB,
  setResponse,
  setShowResponse,
  setShowComponents,
  setModalOpen,
  onCreateSuccess,
  refetchDatasets,
}: DatabaseListBodyProps) => {
  const [sessionToConfirm, setSessionToConfirm] = useState<IDataSource>();
  const { mutate: checkSession, isLoading: isCheckSessionLoading } =
    useMutation(checkSearchSession);

  const { data: latestSearch, isLoading: isLatestSearchLoading } = useQuery({
    queryKey: [QUERIES.LATEST_SEARCH],
    queryFn: () => getLatestSearch(),
  });

  const [latestSessionLoading, setLatestSessionLoading] = useState(false);

  const { mutate: getSession } = useMutation(getSearchSession);
  const { mutate: getSettings } = useMutation(getSearchSettings);
  const { mutate: getRecentHistory } = useMutation(getRecentSearch);
  const [current, setCurrent] = useState(0);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [iLoading, setILoading] = useState(false);
  const [formMode, setFormMode] = useState<"update" | "create">("create");
  const [failed, setFailed] = useState(false);
  const [dataSource, setDataSource] = useState<IDataSourceApp | null>(null);
  const [dataSourceloading, setDataSourceloading] = useState(false);
  const [clickedDatabaseId, setClickedDatabaseId] = useState<string>("");
  const [showAll, setShowAll] = useState(false);
  const [visibleItems, setVisibleItems] = useState(4);

  const navigate = useNavigate();

  // TODO : update with openNotification function in lib so it accepts an action button

  const [api, contextHolder] = notification.useNotification({
    stack: {
      threshold: 1,
    },
  });

  const checkSubscriptionBtn = (
    <StyledNotifBtn
      onClick={() => {
        useAppLayout.setState({ isSmartSearchVisible: false });
        useAppLayout.setState({ isResponseVisible: false });
        navigate("/profile/preferences/subscription-settings");
      }}
    >
      Check Subscription
    </StyledNotifBtn>
  );

  const handleDBClick = (dataset: IDataSource) => {
    setSessionToConfirm(undefined);
    setDataSourceloading(true);
    setClickedDatabaseId(dataset.id ?? "");
    if (dataset.status === DataSourceStatus.DEACTIVATE) {
      api.open({
        message: "Info",
        description:
          "Data source is deactivated, please check your subscription status",
        btn: checkSubscriptionBtn,
        icon: (
          <span style={{ color: "#1677FF" }}>
            <NotificationIcon />
          </span>
        ),
        duration: null,
      });
      setSelectedDB(null);
      setDataSourceloading(false);
      setClickedDatabaseId("");
    } else if (
      dataset.internalType === FileType.EXCEL &&
      isEmpty(dataset.excelValidSheets)
    ) {
      api.open({
        message: "Info",
        description:
          "The excel file does not contain a valid sheet for search.",
        icon: (
          <span style={{ color: "#1677FF" }}>
            <NotificationIcon />
          </span>
        ),
      });
      setSelectedDB(null);
      setDataSourceloading(false);
      setClickedDatabaseId("");
    } else if (dataset.id && !isCheckSessionLoading) {
      if (!selectedDB || selectedDB.id !== dataset.id) {
        if (selectedDB) {
          setSelectedDB(null);
        }
        checkSession(dataset.id, {
          onSuccess: (status) => {
            switch (status) {
              case 200:
                setClickedDatabaseId("");
                setSessionToConfirm(dataset);
                setSelectedDB({ ...dataset, reset: true });
                break;
              case 201:
                setClickedDatabaseId("");
                openNotification(
                  "New session has been created successfully",
                  "info"
                );
                setSessionToConfirm(undefined);
                setSelectedDB({ ...dataset, reset: false });
                setDataSourceloading(false);
                setClickedDatabaseId("");
                break;
            }
          },
          onError: () => {
            setDataSourceloading(false);
            setClickedDatabaseId("");
          },
        });
      } else {
        setSelectedDB(null);
        setDataSourceloading(false);
        setClickedDatabaseId("");
      }
    }
  };

  const handleContinueSession = (id?: string) => {
    if (sessionToConfirm?.id) {
      setDataSourceloading(false);
      setClickedDatabaseId("");
      getSession(sessionToConfirm.id, {
        onSuccess: (data) => {
          setResponse((prev) => ({
            ...prev,
            id: data.searchId,
            data: data.data,
            summary: data.summary,
            settingsId: data.settingsId,
            suggestion: data.suggestions,
            suggestion_question_settings: data.settingsSuggestions,
            query: data.query,
          }));
          if (data?.settingsId) {
            getSettings(data.settingsId, {
              onSuccess: (settings) => {
                setResponse((prev) => ({
                  ...prev,
                  chartType: settings.chartType ?? chartType.DEFAULT,
                  columnName: settings.columnName,
                  rows: settings.rows,
                  hasLegend: settings.hasLegend,
                  colors: settings.colors,
                  title: settings.title,
                  subTitle: settings.subTitle,
                  dataSource: settings.dataSource,
                  cardValuePosition: settings.cardValuePosition,
                  cardIntroValue: settings.cardIntroValue,
                  cardOutroValue: settings.cardOutroValue,
                  // default card settings
                  cardIntroBold: true,
                  cardIntroFontSize: 27,
                  cardValueBold: true,
                  cardValueColor: "#4974f6",
                  cardValueFontSize: 35,
                  cardImage:
                    "https://api-preprod.onegate.ai/api/storage/public/20240418093156627_ai2.png",
                  // default chart settings
                  enableFormattedNumbers: true,
                  displayChartLabels: true,
                }));

                sessionToConfirm.id &&
                  getRecentHistory(
                    { body: sessionToConfirm.id, pageNo: 0 },
                    {
                      onSuccess: (history) => {
                        setResponse((prev) => ({
                          ...prev,
                          recentSearches: history.content,
                        }));
                        if (sessionToConfirm) {
                          useAppLayout.setState({ isResponseVisible: true });
                          setShowResponse(true);
                          setShowComponents(false);
                          setSelectedDB({
                            ...sessionToConfirm,
                            reset: false,
                          });
                          setSessionToConfirm(undefined);
                        }
                      },
                    }
                  );
              },
            });
          } else {
            sessionToConfirm.id &&
              getRecentHistory(
                { body: sessionToConfirm.id, pageNo: 0 },
                {
                  onSuccess: (history) => {
                    setResponse((prev) => ({
                      ...prev,
                      recentSearches: history.content,
                    }));
                    if (sessionToConfirm) {
                      useAppLayout.setState({ isResponseVisible: true });
                      setShowResponse(true);
                      setShowComponents(false);
                      setSelectedDB({
                        ...sessionToConfirm,
                        reset: false,
                      });
                      setSessionToConfirm(undefined);
                    }
                  },
                }
              );
          }
        },
        onError: () => {
          openNotification(
            "No history found, you can do your first search",
            "info"
          );
          setSelectedDB({ ...sessionToConfirm, reset: false });
          setSessionToConfirm(undefined);
        },
      });
    }
    if (id) {
      const dataset = datasources?.content?.find(
        (dataset) => dataset.id === id
      );
      if (!dataset) return;
      setLatestSessionLoading(true);
      getSession(id, {
        onSuccess: (data) => {
          setResponse((prev) => ({
            ...prev,
            id: data.searchId,
            data: data.data,
            summary: data.summary,
            settingsId: data.settingsId,
            suggestion: data.suggestions,
            suggestion_question_settings: data.settingsSuggestions,
            query: data.query,
          }));
          if (data?.settingsId) {
            getSettings(data.settingsId, {
              onSuccess: (settings) => {
                setResponse((prev) => ({
                  ...prev,
                  chartType: settings.chartType ?? chartType.DEFAULT,
                  columnName: settings.columnName,
                  rows: settings.rows,
                  hasLegend: settings.hasLegend,
                  colors: settings.colors,
                  title: settings.title,
                  subTitle: settings.subTitle,
                  dataSource: settings.dataSource,
                  cardValuePosition: settings.cardValuePosition,
                  cardIntroValue: settings.cardIntroValue,
                  cardOutroValue: settings.cardOutroValue,
                  // default card settings
                  cardIntroBold: true,
                  cardIntroFontSize: 27,
                  cardValueBold: true,
                  cardValueColor: "#4974f6",
                  cardValueFontSize: 35,
                  cardImage:
                    "https://api-preprod.onegate.ai/api/storage/public/20240418093156627_ai2.png",
                  // default chart settings
                  enableFormattedNumbers: true,
                  displayChartLabels: true,
                }));

                id &&
                  getRecentHistory(
                    { body: id, pageNo: 0 },
                    {
                      onSuccess: (history) => {
                        setResponse((prev) => ({
                          ...prev,
                          recentSearches: history.content,
                        }));
                        useAppLayout.setState({ isResponseVisible: true });
                        setShowResponse(true);
                        setShowComponents(false);
                        setSelectedDB({ ...dataset, reset: false });
                        setLatestSessionLoading(false);
                      },
                    }
                  );
              },
            });
          } else {
            id &&
              getRecentHistory(
                { body: id, pageNo: 0 },
                {
                  onSuccess: (history) => {
                    setResponse((prev) => ({
                      ...prev,
                      recentSearches: history.content,
                    }));
                    useAppLayout.setState({ isResponseVisible: true });
                    setShowResponse(true);
                    setShowComponents(false);
                    setSelectedDB({ ...dataset, reset: false });
                    setLatestSessionLoading(false);
                  },
                }
              );
          }
        },
        onError: () => {
          openNotification(
            "No history found, you can do your first search",
            "info"
          );
          setLatestSessionLoading(false);
        },
      });
    }
  };

  const showModal = () => {
    setOpen(true);
    setFormMode("create");
  };

  const handleOk = () => {
    setConfirmLoading(true);
    setTimeout(() => {
      setOpen(false);
      setConfirmLoading(false);
    }, 2000);
  };

  const handleCancel = () => {
    setCurrent(0);
    setOpen(false);
    setFormMode("create");
    setFailed(false);
  };

  const handleAddMore = () => {
    setCurrent(1);
    setFormMode("create");
  };

  const handleGoBack = () => {
    if (!failed) {
      setCurrent(current - 1);
    }
    setFailed(false);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    setVisibleItems(showAll ? 4 : datasources?.content?.length || 4);
  };

  const viewButton =
    isLoading || (datasources?.content?.length || 0) < 5 ? (
      ""
    ) : (
      <StyledView onClick={toggleShowAll}>
        {showAll ? "View less" : "View all"}
      </StyledView>
    );
  const dataSourceItems = datasources?.content
    ? datasources.content.slice(0, visibleItems).map((database) => (
        <Col key={database.id}>
          <StyledDataBase
            $dataSourceloading={dataSourceloading}
            $deactivated={
              database.status === DataSourceStatus.DEACTIVATE ||
              (database.internalType === FileType.EXCEL &&
                isEmpty(database.excelValidSheets))
            }
          >
            <div
              className={`database ${
                selectedDB?.id === database.id && "selected"
              } ${
                (database.status === DataSourceStatus.DEACTIVATE ||
                  (database.internalType === FileType.EXCEL &&
                    isEmpty(database.excelValidSheets))) &&
                "disabled-database"
              }`}
              onClick={() => handleDBClick(database)}
            >
              <div>
                {dataSourceloading && clickedDatabaseId === database.id ? (
                  <img
                    height={35}
                    alt="dataset"
                    src="images/smart-search/ds-loading.gif"
                  />
                ) : (
                  <StyledIcon>
                    {database.internalType === DatabaseType.MYSQL ? (
                      <MysqlIcon />
                    ) : database.internalType === DatabaseType.SQLSERVER ? (
                      <SqlServerIcon />
                    ) : database.internalType === DatabaseType.SAGE ? (
                      <SageIcon />
                    ) : database.internalType === DatabaseType.POSTGRESQL ? (
                      <PostgresqlIcon />
                    ) : database.internalType === DatabaseType.SAP ? (
                      <SapIcon />
                    ) : database.internalType === FileType.EXCEL ? (
                      <ExcelIcon />
                    ) : (
                      <CsvIcon />
                    )}
                  </StyledIcon>
                  // <img
                  //   height={35}
                  //   alt="dataset"
                  //   src={`images/data-management/datasources_icons/icon-${
                  //     database.internalType === DatabaseType.MYSQL
                  //       ? "mysql"
                  //       : database.internalType === DatabaseType.SQLSERVER
                  //       ? "sql-server"
                  //       : database.internalType === DatabaseType.MONGODB
                  //       ? "mongodb"
                  //       : database.internalType === DatabaseType.POSTGRESQL
                  //       ? "postgresql"
                  //       : database.internalType === DatabaseType.ORACLE
                  //       ? "oracle"
                  //       : database.internalType === FileType.EXCEL
                  //       ? "sheets"
                  //       : "csv"
                  //   }.svg`}
                  // />
                )}
              </div>
            </div>
            <Tooltip
              placement="bottom"
              title={
                <span style={{ fontSize: "12px" }}>{database.displayName}</span>
              }
              overlayStyle={{ maxWidth: "100px" }}
            >
              <div
                className={`name ${
                  (database.status === DataSourceStatus.DEACTIVATE ||
                    (database.internalType === FileType.EXCEL &&
                      isEmpty(database.excelValidSheets))) &&
                  "disabled-database"
                }`}
              >
                {database.displayName}
              </div>
            </Tooltip>
          </StyledDataBase>
        </Col>
      ))
    : null;

  return (
    <StyledDatabaseListBody ref={stepRef4}>
      {contextHolder}
      {!!sessionToConfirm && (
        <DatabaseSession
          dataSourceloading={dataSourceloading}
          handleContinueSession={handleContinueSession}
        />
      )}
      {!sessionToConfirm &&
        !selectedDB &&
        !dataSourceloading &&
        (isLatestSearchLoading ? (
          <Skeleton.Input
            active
            block
            size="small"
            style={{ width: "560px", marginBottom: "4px" }}
          />
        ) : (
          latestSearch?.data && (
            <DatabaseSession
              latestSearch={latestSearch?.data}
              dataSourceloading={!latestSessionLoading}
              handleContinueSession={handleContinueSession}
            />
          )
        ))}
      <StyledRow
        exist={
          selectedDB !== null && selectedDB.dataSourceType === "DATABASE"
            ? true
            : false
        }
      >
        <Row justify="start">
          <Col className="add">
            <StyledAdd ref={stepRef3} onClick={showModal}>
              <PlusIcon />
            </StyledAdd>
          </Col>
          {isLoading ? (
            <StyledLoading>
              <Loading />
            </StyledLoading>
          ) : (
            dataSourceItems
          )}
        </Row>
      </StyledRow>
      {viewButton}
      <Suspense fallback={<></>}>
        <DataSourceModal
          modalTitle="Create a new data source"
          containerId="#smartsearch-modal"
          open={isOpen}
          confirmLoading={confirmLoading}
          handleOk={handleOk}
          handleCancel={handleCancel}
          handleGoBack={handleGoBack}
          handleAddMore={handleAddMore}
          isDeleteModal={false}
          failed={failed}
          formMode={formMode}
          children={
            <DataSourceStepsForm
              dataSourceData={null}
              setCurrent={setCurrent}
              current={current}
              iLoading={iLoading}
              setILoading={setILoading}
              failed={failed}
              dataSource={dataSource}
              setDataSource={setDataSource}
              setFailed={setFailed}
              formMode={formMode}
              onCreateSuccess={onCreateSuccess}
              refetchDatasets={refetchDatasets}
            />
          }
          current={current}
        />
      </Suspense>
      <DataSourcesModal
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
        datasources={datasources}
        isLoading={isLoading}
        selectedDB={selectedDB}
        handleDBClick={handleDBClick}
        dataSourceLoading={dataSourceloading}
        clickedDatabaseId={clickedDatabaseId ?? ""}
      />
      {/* <SessionModal
        open={!!sessionToConfirm}
        onCancel={() => {
          setSessionToConfirm(undefined);
          setDataSourceloading(false);
          setClickedDatabaseId("");
        }}
        content={<SessionConfirm />}
        footer={SessionConfirmFooter}
      /> */}
    </StyledDatabaseListBody>
  );
};

export default DatabaseListBody;

const StyledDatabaseListBody = styled.div`
  // display: flex;
  // margin-top: 15px;
  margin-top: 10px;
  // position: relative;
  .ant-row {
    width: 100%;
    align-items: center;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    @media (max-width: 860px) {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }
  }
  .ant-col {
    width: 105px;
  }
`;

const StyledLoading = styled.div`
  position: fixed;
  margin-top: -200px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 580px;
`;

const StyledDataBase = styled.div<{
  $dataSourceloading: boolean;
  $deactivated: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 3px 10px;
  cursor: pointer;
  .database {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 14px 28px;
    background: #eff1fd;
    border-radius: 10px;
    height: 64px;
    &.disabled-database {
      opacity: 0.6;
      cursor: not-allowed;
    }
    &.selected {
      background: #fff;
      border-radius: 10px;
      color: #4974f6;
      width: 100%;
      border: 1.5px solid #eff1fd;
    }
    img {
      width: 35px;
      height: 35px;
    }
    ${({ $deactivated }) =>
      !$deactivated &&
      `&:hover {
  background: #fff;
  border-radius: 10px;
  border: 1.5px solid #eff1fd;
}`}
  }
  .name {
    padding-top: 10px;
    max-width: 90px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    &.disabled-database {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const StyledSessionConfirmFooter = styled.div<{ $dataSourceloading: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  .continue-btn {
    border-radius: 10px;
    background: #4974f6 !important;
    // box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset;
    color: #fff !important;
    font-size: 12px;
    cursor: ${(props) =>
      props.$dataSourceloading ? "pointer" : "not-allowed"};
  }
  .reset-btn {
    border-radius: 10px;
    color: rgba(0, 0, 0, 0.88) !important;
    background: #eff1fd;
    border: 0;
    font-size: 12px;
  }
`;

const StyledAdd = styled.div`
  cursor: pointer;
  margin: 10px;
  border: 1.5px solid #eff1fd;
  border-radius: 10px;
  color: #4974f6;
  padding: 21px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  // height: 88px;
  svg {
    display: flex;
  }
`;

const StyledNotifBtn = styled(Button)`
  background-color: #4974f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  height: 32px;
  font-size: 13px;
  &:hover {
    color: #fff !important;
    border: none;
    opacity: 0.8;
  }
`;
const StyledView = styled.div`
  direction: rtl;
  margin-top: 5px;
  cursor: pointer;
  color: #4974f6;
  font-size: 14px;
  font-weight: 500;
`;

const StyledRow = styled.div<{ exist: boolean }>`
  .add {
    height: 107px;
  }
`;

const StyledIcon = styled.div`
  display: flex;
  font-size: 34px;
`;
